import { API_BASE_URL } from './api';
import { authService } from './authService';
import { Dive } from '../types/Dive';

export interface SyncResponse {
  success: boolean;
  totalDives: number;
  newDives: number;
  updatedDives: number;
  lastSyncAt: string;
}

export interface ConnectSuuntoResponse {
  success: boolean;
  message: string;
  user?: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    createdAt: string;
    lastLoginAt?: string;
    hasSuuntoConnection: boolean;
    lastSyncAt?: string;
  };
}

export interface CreateDiveRequest {
  startTime: string;
  durationMinutes: number;
  maxDepth: number;
  location?: string;
  diveName?: string; // User-defined name for this specific dive
  avgDepth?: number;
  waterTemperature?: number;
  diveType?: string;
  latitude?: number;
  longitude?: number;
  safetyStopMinutes?: number;
  visibility?: string;
  weightUsed?: number;
  weightUnit?: string;
  airConsumed?: number;
  pressureUnit?: string;
  tankSize?: string;
  customTankVolume?: number;
  startPressure?: number;
  endPressure?: number;
  manualRmv?: number; // Surface RMV in L/min entered directly by the user
  suitType?: string;
  suitThicknessMm?: number;
  equipmentIds?: string[];
}

export interface CreateDiveResponse {
  success: boolean;
  dive: Dive;
}

export interface ImportDiveFileResponse {
  success: boolean;
  totalDives: number;
  newDives: number;
  skippedDives: number;
}

class DiveService {
  async getMyDives(): Promise<Dive[]> {
    const response = await fetch(`${API_BASE_URL}/dives/my-dives`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized - please log in again');
      }
      const errorText = await response.text();
      throw new Error(`Failed to fetch dives: ${response.status} ${errorText}`);
    }

    return await response.json();
  }

  async getMyDive(diveId: string): Promise<Dive> {
    const response = await fetch(`${API_BASE_URL}/dives/my-dives/${diveId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Dive not found');
      }
      throw new Error('Failed to fetch dive');
    }

    return await response.json();
  }

  async syncFromSuunto(): Promise<SyncResponse> {
    const response = await fetch(`${API_BASE_URL}/dives/sync-from-suunto`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sync dives');
    }

    return await response.json();
  }

  async importDiveFile(file: File): Promise<ImportDiveFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // No Content-Type header: the browser sets the multipart boundary itself
    const response = await fetch(`${API_BASE_URL}/dives/import`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to import dive file');
    }

    return await response.json();
  }

  async connectSuuntoAccount(accessToken: string, suuntoUserId?: string): Promise<ConnectSuuntoResponse> {
    const response = await fetch(`${API_BASE_URL}/dives/connect-suunto`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
      body: JSON.stringify({ accessToken, suuntoUserId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to connect Suunto account');
    }

    return await response.json();
  }

  async createDive(diveData: CreateDiveRequest): Promise<CreateDiveResponse> {
    const response = await fetch(`${API_BASE_URL}/dives`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
      body: JSON.stringify(diveData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create dive');
    }

    return await response.json();
  }

  async deleteDive(diveId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/dives/my-dives/${diveId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete dive');
    }

    return await response.json();
  }

  async deleteAllDives(): Promise<{ success: boolean; message: string; deletedCount: number }> {
    const response = await fetch(`${API_BASE_URL}/dives/my-dives`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete all dives');
    }

    return await response.json();
  }

  async refreshDiveFromSuunto(diveId: string): Promise<{ success: boolean; message: string; dive: any }> {
    const response = await fetch(`${API_BASE_URL}/dives/my-dives/${diveId}/refresh-from-suunto`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to refresh dive from Suunto');
    }

    return await response.json();
  }

  async updateDive(diveId: string, updates: Partial<CreateDiveRequest>): Promise<{ success: boolean; dive: Dive }> {
    const response = await fetch(`${API_BASE_URL}/dives/my-dives/${diveId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update dive');
    }

    return await response.json();
  }
}

export const diveService = new DiveService();
